// Resolves WHICH CmForm version a row is captured/edited with — from the row's own
// provenance tags, not a global "latest" pick:
//
//   • `row_data._form_uuid`     — the exact CmForm version the row was captured with
//                                 (create or most-recent write). Fetched via `useForm`,
//                                 so it resolves even if the version was since archived.
//                                 This is the "Original form" (as-submitted) source.
//   • `row_data._form_group_id` — the stable logical-form id across versions. Used to
//                                 pick the "Latest version" published form of the SAME
//                                 form group (not just any form on the table).
//
// A table can bind more than one form (disjoint fields), so a global "highest version"
// pick would render the wrong form's inputs for a given row. When the as-submitted and
// latest-published versions differ, the host offers a toggle. Untagged/legacy rows fall
// back to a manual pick (auto-picked when the table has exactly one form).
//
// Lives outside the renderer because EntityForm has to know the resolved schema BEFORE
// it lays the record out: a form that supplies the row's title/description replaces
// EntityForm's own Name / Description inputs.

import { useMemo, useState } from "react";
import { useForm, usePublishedFormsForTable } from "../../../api/fieldForms";
import { FieldForm } from "../../../types/fieldForm";

/** Which of the two resolved candidates the user is currently looking at. */
export type FormVersionSelection = "submitted" | "latest";

export interface ResolvedRowForm {
  /** The form whose schema should be rendered + saved against (null while unresolved). */
  activeForm: FieldForm | null;
  /** Every published form bound to the table (the manual picker's options). */
  published: FieldForm[];
  /** True while the table's forms are still being fetched. */
  isLoading: boolean;
  /** True when no form could be resolved but the table has forms to choose from. */
  needsPicker: boolean;
  /** True when the row carries no `_form_uuid` / `_form_group_id` provenance tag. */
  hasTag: boolean;
  /** True when the row's captured version genuinely differs from the group's latest. */
  showVersionToggle: boolean;
  selection: FormVersionSelection;
  setSelection: (next: FormVersionSelection) => void;
  setManualForm: (form: FieldForm) => void;
}

interface Params {
  /** Bound CmTable slug — null/"" disables the lookup. */
  tableSlug: string | null;
  /** The row's `row_data` (carries the provenance tags). */
  rowData: Record<string, any>;
}

export function useResolvedRowForm({ tableSlug, rowData }: Params): ResolvedRowForm {
  const { data, isLoading } = usePublishedFormsForTable(tableSlug || null);

  // Row provenance tags (reserved keys stamped by Field sync / the row endpoints).
  const formUuid = (rowData?._form_uuid as string | undefined) ?? null;
  const formGroupId = (rowData?._form_group_id as string | undefined) ?? null;
  const hasTag = Boolean(formUuid || formGroupId);

  // "Original form" — the exact version this row was captured with. Resolves even
  // when since archived (`useForm` isn't published-only).
  const { data: asSubmittedForm } = useForm(formUuid);

  const published = useMemo<FieldForm[]>(() => data?.results ?? [], [data]);

  // "Latest version" — highest published version WITHIN the row's form group. When
  // the row has no group tag we can't scope, so fall back to the table-wide latest.
  const latestPublishedForm = useMemo<FieldForm | null>(() => {
    const list = formGroupId ? published.filter((f) => f.form_group_id === formGroupId) : published;
    return list.reduce<FieldForm | null>((latest, f) => (!latest || f.version > latest.version ? f : latest), null);
  }, [published, formGroupId]);

  // Which schema renders when both candidates resolve. Defaults to "submitted".
  const [selection, setSelection] = useState<FormVersionSelection>("submitted");
  // Manual pick for untagged/legacy rows (no _form_uuid and no _form_group_id).
  const [manualForm, setManualForm] = useState<FieldForm | null>(null);

  const activeForm = useMemo<FieldForm | null>(() => {
    if (asSubmittedForm && selection === "submitted") return asSubmittedForm;
    if (latestPublishedForm && selection === "latest") return latestPublishedForm;
    // Group known but the exact version couldn't be fetched — use the group's latest.
    if (asSubmittedForm) return asSubmittedForm;
    if (formGroupId && latestPublishedForm) return latestPublishedForm;
    // Untagged: a user pick, or an implicit auto-pick when the table has one form.
    if (manualForm) return manualForm;
    if (!hasTag && published.length === 1) return published[0];
    return null;
  }, [asSubmittedForm, latestPublishedForm, selection, formGroupId, manualForm, published, hasTag]);

  return {
    activeForm,
    published,
    isLoading: isLoading && published.length === 0 && !asSubmittedForm,
    needsPicker: !activeForm && published.length > 0,
    hasTag,
    showVersionToggle: Boolean(asSubmittedForm && latestPublishedForm && asSubmittedForm.uuid !== latestPublishedForm.uuid),
    selection,
    setSelection,
    setManualForm
  };
}
